import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { transformSDKTimestampToRESTTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveComWixpressBookingsAvailabilityV2EventTimeSlotsUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'www.wixapis.com': [
      {
        srcPath: '/_api/service-availability/v2/time-slots',
        destPath: '/v2/time-slots',
      },
      {
        srcPath: '/_api/service-availability/v2/multi-service-time-slots',
        destPath: '/v2/multi-service-time-slots',
      },
      {
        srcPath: '/service-availability/v2/time-slots',
        destPath: '/v2/time-slots',
      },
      {
        srcPath: '/service-availability/v2/multi-service-time-slots',
        destPath: '/v2/multi-service-time-slots',
      },
    ],
    '*.dev.wix-code.com': [
      {
        srcPath: '/_api/service-availability/v2/time-slots',
        destPath: '/v2/time-slots',
      },
      {
        srcPath: '/_api/service-availability/v2/multi-service-time-slots',
        destPath: '/v2/multi-service-time-slots',
      },
      {
        srcPath: '/service-availability/v2/time-slots',
        destPath: '/v2/time-slots',
      },
      {
        srcPath: '/service-availability/v2/multi-service-time-slots',
        destPath: '/v2/multi-service-time-slots',
      },
    ],
    'manage._base_domain_': [
      {
        srcPath: '/_api/service-availability/v2/time-slots',
        destPath: '/v2/time-slots',
      },
      {
        srcPath: '/_api/service-availability/v2/multi-service-time-slots',
        destPath: '/v2/multi-service-time-slots',
      },
      {
        srcPath: '/service-availability/v2/multi-service-time-slots',
        destPath: '/v2/multi-service-time-slots',
      },
      {
        srcPath: '/service-availability/v2/time-slots',
        destPath: '/v2/time-slots',
      },
    ],
    'www._base_domain_': [
      {
        srcPath: '/_api/service-availability/v2/time-slots',
        destPath: '/v2/time-slots',
      },
      {
        srcPath: '/_api/service-availability/v2/multi-service-time-slots',
        destPath: '/v2/multi-service-time-slots',
      },
    ],
    _: [
      {
        srcPath: '/_api/service-availability/v2/time-slots',
        destPath: '/v2/time-slots',
      },
      {
        srcPath: '/_api/service-availability/v2/multi-service-time-slots',
        destPath: '/v2/multi-service-time-slots',
      },
      {
        srcPath: '/_api/service-availability/v2/time-slots/event',
        destPath: '/v2/time-slots/event',
      },
    ],
    'editor._base_domain_': [
      {
        srcPath: '/_api/service-availability/v2/multi-service-time-slots',
        destPath: '/v2/multi-service-time-slots',
      },
      {
        srcPath: '/_api/service-availability/v2/time-slots',
        destPath: '/v2/time-slots',
      },
      {
        srcPath: '/_api/service-availability/v2/time-slots/event',
        destPath: '/v2/time-slots/event',
      },
    ],
    'blocks._base_domain_': [
      {
        srcPath: '/_api/service-availability/v2/multi-service-time-slots',
        destPath: '/v2/multi-service-time-slots',
      },
      {
        srcPath: '/_api/service-availability/v2/time-slots',
        destPath: '/v2/time-slots',
      },
      {
        srcPath: '/_api/service-availability/v2/time-slots/event',
        destPath: '/v2/time-slots/event',
      },
    ],
    'create.editorx': [
      {
        srcPath: '/_api/service-availability/v2/multi-service-time-slots',
        destPath: '/v2/multi-service-time-slots',
      },
      {
        srcPath: '/_api/service-availability/v2/time-slots',
        destPath: '/v2/time-slots',
      },
      {
        srcPath: '/_api/service-availability/v2/time-slots/event',
        destPath: '/v2/time-slots/event',
      },
    ],
    'editor.wixapps.net': [
      {
        srcPath: '/_api/service-availability/v2/time-slots/event',
        destPath: '/v2/time-slots/event',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_bookings_event-time-slots';

/**
 * Retrieves a list of class session time slots that match the provided filters.
 *
 * ### Defaults
 *
 * List Event Time Slots uses the following defaults:
 *
 * - `providerId` set to the Bookings app ID.
 * - `timeZone` set to the business time zone.
 * - `cursorPaging.limit` set to `50`.
 * - Returns both bookable and non-bookable time slots.
 *
 * Time slots are always sorted by their `localStartDate` in ascending order.
 *
 * ### Service type limitations
 *
 * To retrieve appointment availability, call [List Availability Time Slots](https://dev.wix.com/docs/api-reference/business-solutions/bookings/time-slots/time-slots-v2/list-availability-time-slots).
 *
 * To retrieve course availability, follow the [End-to-End Booking Flow for courses](https://dev.wix.com/docs/api-reference/business-solutions/bookings/end-to-end-booking-flows#book-a-course).
 *
 * ### Booking policy filtering
 *
 * Use `bookingPolicyViolations` to filter slots based on booking restrictions defined in your [booking policies](https://dev.wix.com/docs/api-reference/business-solutions/bookings/policies/booking-policies/introduction).
 *
 * *Default behavior**: When you don't specify any `bookingPolicyViolations` filters, all slots are returned regardless of their booking policy status.
 *
 * - Set `tooEarlyToBook` to `true` to retrieve slots that can't be booked yet due to minimum advance booking time restrictions.
 * Currently, you can't specify `earliestBookingDate` to further filter by when the slot becomes bookable.
 * - Set `tooLateToBook` to `true` to retrieve slots that can no longer be booked due to maximum advance booking time restrictions.
 * - Set `bookOnlineDisabled` to `true` or `false` to filter by online booking availability settings.
 */
export function listEventTimeSlots(
  payload: object
): RequestOptionsFactory<any> {
  function __listEventTimeSlots({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [{ path: 'bookingPolicyViolations.earliestBookingDate' }],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.bookings.availability.v2.time_slot',
      method: 'POST' as any,
      methodFqn:
        'com.wixpress.bookings.availability.v2.EventTimeSlots.ListEventTimeSlots',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressBookingsAvailabilityV2EventTimeSlotsUrl({
        protoPath: '/v2/time-slots/event',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'timeSlots.bookingPolicyViolations.earliestBookingDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __listEventTimeSlots;
}

/**
 * Retrieves detailed information about a specific class session time slot.
 *
 *
 * Call this method after selecting a suitable slot with List Event Time Slots to obtain its full capacity and booking-policy status.
 *
 * ### Service type limitations
 *
 * To retrieve appointment availability, call [Get Availability Time Slot](https://dev.wix.com/docs/api-reference/business-solutions/bookings/time-slots/time-slots-v2/get-availability-time-slot).
 *
 * To retrieve course availability, follow the [End-to-End Booking Flow for courses](https://dev.wix.com/docs/api-reference/business-solutions/bookings/end-to-end-booking-flows#book-a-course).
 */
export function getEventTimeSlot(payload: object): RequestOptionsFactory<any> {
  function __getEventTimeSlot({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.bookings.availability.v2.time_slot',
      method: 'GET' as any,
      methodFqn:
        'com.wixpress.bookings.availability.v2.EventTimeSlots.GetEventTimeSlot',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressBookingsAvailabilityV2EventTimeSlotsUrl({
        protoPath: '/v2/time-slots/event/{eventId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'timeSlot.bookingPolicyViolations.earliestBookingDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __getEventTimeSlot;
}
